import React, { useState, useEffect, useRef } from 'react';
import {
  FILE_ACCEPT,
  MAX_TEXT_LENGTH,
  MAX_TOTAL_FILE_SIZE,
  MAX_SINGLE_FILE_SIZE,
  UPLOAD_HINT,
  isAllowedFileType,
  formatFileSize,
} from '../../../constants/upload-limits';
import {
  DockWrapper,
  DockHandleWrapper,
  DockHandle,
  DockTextArea,
  DockActions,
  DockItem,
  DockIcon,
  AttachedFiles,
  AttachedFile,
  AttachedFileName,
  AttachedCancelIcon,
  AttachIcon,
  AttachedFileContainer,
  AttachFileUploadingProgress,
  CharacterCount,
  UploadHint,
  FileError,
} from './type-dock.styled';

export interface AttachedFile {
  id: string;
  name: string;
  file: File;
  isUploading?: boolean;
  uploadProgress?: number;
}

interface TypeDockProps {
  onClose: (text?: string) => void;
  onSubmit: (text: string, files?: File[]) => void;
  initialText?: string;
  onRecordClick?: () => void;
  attachedFiles?: AttachedFile[];
  onFilesChange?: (files: AttachedFile[]) => void;
  disabled?: boolean;
  uploadProgress?: number | null | undefined;
  isKeyboardOpen?: boolean;
}

export const TypeDock: React.FC<TypeDockProps> = ({
  onClose,
  onSubmit,
  initialText = '',
  onRecordClick,
  attachedFiles: controlledFiles,
  onFilesChange,
  disabled = false,
  uploadProgress,
  isKeyboardOpen = false,
}) => {
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localFiles, setLocalFiles] = useState<AttachedFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn] = useState(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const [isSlidingOut, setIsSlidingOut] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const isDragging = useRef(false);

  const DRAG_THRESHOLD = 80;

  const handleDragStart = (clientY: number) => {
    dragStartY.current = clientY;
    isDragging.current = false;
  };

  const handleDragMove = (clientY: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY - dragStartY.current;
    if (delta > 5) isDragging.current = true;
    setDragOffsetY(Math.max(0, delta));
  };

  const handleDragEnd = (clientY?: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY != null ? clientY - dragStartY.current : 0;
    const offset = Math.max(0, delta);
    dragStartY.current = null;
    if (offset >= DRAG_THRESHOLD) {
      setIsSlidingOut(true);
      setDragOffsetY(999);
      onClose(text);
    } else if (offset > 0) {
      setIsSnappingBack(true);
      setDragOffsetY(0);
      setTimeout(() => setIsSnappingBack(false), 200);
    }
  };

  const attachedFiles = controlledFiles ?? localFiles;
  const setAttachedFiles = (files: AttachedFile[] | ((prev: AttachedFile[]) => AttachedFile[])) => {
    if (onFilesChange) {
      if (typeof files === 'function') {
        onFilesChange(files(attachedFiles));
      } else {
        onFilesChange(files);
      }
    } else {
      setLocalFiles(files);
    }
  };

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '56px';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [text]);

  const handleSubmit = () => {
    if (disabled) return;
    if (text.trim() || attachedFiles.length > 0) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        const files = attachedFiles.map((f) => f.file);
        onSubmit(text, files.length > 0 ? files : undefined);

        // Reset state
        setText('');
        setAttachedFiles([]);

        onClose();
      }, 300);
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    setFileError(null);

    const currentSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0);
    const accepted: AttachedFile[] = [];
    const rejected: string[] = [];
    let runningSize = currentSize;

    for (const file of Array.from(files)) {
      const isDuplicate = attachedFiles.some(
        (f) => f.name === file.name && f.file.size === file.size,
      ) || accepted.some((f) => f.name === file.name && f.file.size === file.size);
      if (isDuplicate) {
        rejected.push(`"${file.name}" is already attached`);
        continue;
      }
      if (!isAllowedFileType(file)) {
        rejected.push(`"${file.name}" is not a supported file type`);
        continue;
      }
      if (file.size > MAX_SINGLE_FILE_SIZE) {
        rejected.push(`"${file.name}" exceeds ${formatFileSize(MAX_SINGLE_FILE_SIZE)} per file limit`);
        continue;
      }
      if (runningSize + file.size > MAX_TOTAL_FILE_SIZE) {
        rejected.push(`"${file.name}" exceeds the ${formatFileSize(MAX_TOTAL_FILE_SIZE)} limit`);
        continue;
      }
      accepted.push({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        file,
        isUploading: false,
        uploadProgress: 0,
      });
      runningSize += file.size;
    }

    if (accepted.length > 0) {
      setAttachedFiles((prev) => [...prev, ...accepted]);
    }
    if (rejected.length > 0) {
      setFileError(rejected.join(". "));
    }
    event.target.value = '';
  };

  const handleRemoveFile = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleRecordClick = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      onRecordClick?.();
    }, 300);
  };

  const handleClose = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      onClose(text);
    }, 300);
  };

  const hasText = text.trim().length > 0;
  const totalFileSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0);
  const sizeRatio = totalFileSize / MAX_TOTAL_FILE_SIZE;

  return (
    <DockWrapper
      isAnimatingOut={isAnimatingOut}
      isAnimatingIn={isAnimatingIn}
      isKeyboardOpen={isKeyboardOpen}
      style={dragOffsetY > 0 ? {
        transform: `translateY(${dragOffsetY}px)`,
        opacity: isSlidingOut ? 0 : Math.max(0.3, 1 - dragOffsetY / 200),
        transition: isSlidingOut ? 'transform 0.2s ease-in, opacity 0.2s ease-in' : 'none',
      } : isSnappingBack ? {
        transform: 'translateY(0px)',
        opacity: 1,
        transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
      } : undefined}
    >
      <DockHandleWrapper
        onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
        onTouchMove={(e) => handleDragMove(e.touches[0].clientY)}
        onTouchEnd={(e) => handleDragEnd(e.changedTouches[0]?.clientY)}
        onMouseDown={(e) => handleDragStart(e.clientY)}
        onMouseMove={(e) => { if (e.buttons === 1) handleDragMove(e.clientY); }}
        onMouseUp={(e) => handleDragEnd(e.clientY)}
        onMouseLeave={(e) => { if (dragStartY.current !== null) handleDragEnd(e.clientY); }}
      >
        <DockHandle onClick={() => { if (!isDragging.current) handleClose(); }} style={{ cursor: 'grab' }} />
      </DockHandleWrapper>
      <AttachedFiles>
        {attachedFiles.map((file) => (
          <AttachedFileContainer key={file.id}>
            <AttachedFile>
              <AttachIcon src="/icons/onboarding/question/file.svg" alt="File Icon" />
              <AttachedFileName>{file.isUploading ? 'Uploading...' : file.name}</AttachedFileName>
              <AttachedCancelIcon
                src="/icons/onboarding/question/cancel-upload.svg"
                alt="Cancel Icon"
                onClick={() => handleRemoveFile(file.id)}
              />
            </AttachedFile>
            {file.isUploading && file.uploadProgress !== undefined && (
              <AttachFileUploadingProgress style={{ width: `${file.uploadProgress}%` }} />
            )}
          </AttachedFileContainer>
        ))}
      </AttachedFiles>
      <DockTextArea
        ref={textareaRef}
        placeholder="Write your response…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={MAX_TEXT_LENGTH}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-form-type="other"
      />
      {text.length > MAX_TEXT_LENGTH * 0.8 && (
        <CharacterCount isNearLimit={text.length > MAX_TEXT_LENGTH * 0.9}>
          {text.length} / {MAX_TEXT_LENGTH}
        </CharacterCount>
      )}
      {uploadProgress != null && uploadProgress > 0 && (
        <div style={{ margin: '0 0 4px' }}>
          <div
            style={{
              height: 3,
              background: '#5E84ED',
              borderRadius: 9999,
              width: `${uploadProgress}%`,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      )}
      {fileError && <FileError>{fileError}</FileError>}
      <UploadHint isNearLimit={sizeRatio > 0.8}>
        {attachedFiles.length > 0
          ? `${formatFileSize(totalFileSize)} / ${formatFileSize(MAX_TOTAL_FILE_SIZE)} used`
          : UPLOAD_HINT}
      </UploadHint>
      <DockActions>
        <DockItem type="button" onClick={handleAttachClick}>
          <DockIcon src="/attach-dark-bg.svg" alt="Attach" />
        </DockItem>
        {hasText ? (
          <DockItem type="button" onClick={handleSubmit} style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
            <DockIcon src="/send-with-bg.svg" alt="Send" />
          </DockItem>
        ) : (
          <DockItem type="button" onClick={handleRecordClick}>
            <DockIcon src="/record-with-bg.svg" alt="Record" />
          </DockItem>
        )}
      </DockActions>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept={FILE_ACCEPT}
      />
    </DockWrapper>
  );
};
